import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CoinLetterTemplate } from '@coin/shared/cms/util';
import { CommunicationStateEnum } from '@coin/shared/communication/util';
import { Season } from '@coin/shared/season-mgmt/util';
import {
  CommunicationWorkflowType,
  IncentiveRecordsWorkflowTemplateTypeEnum,
  SeasonTypeEnum,
  WorkflowType
} from '@coin/shared/core/util-enums';
import { TinyHelpers, WorkflowHelper } from '@coin/shared/core/util-helpers';
import { CommunicationsLetterPublicationsPresetLetterPublicationInfoModel, Workflow } from '@coin/shared/core/util-models';
import moment, { Moment } from 'moment';
import { first, forkJoin, Observable, of, shareReplay } from 'rxjs';
import { map } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { BudgetAllocationService, LetterCreationService } from '@coin/shared/incentive/data-access';
import {
  AddPublicationDate,
  IncentiveLetterLetterPublishSelection,
  IncentiveRecordsManagerTypeEnum,
  LetterSelectionResult
} from '@coin/shared/incentive/util';
import {
  V2ButtonComponent,
  V2CardComponent,
  V2DateInputComponent,
  V2DialogModule,
  V2DropdownComponent
} from '@coin/shared/core/ui-storybook';
import { TranslateModule } from '@ngx-translate/core';
import { LegacyComponentsModule } from '@coin/shared/core/feature-legacy-components';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { EnumToTextPipe } from '@coin/shared/core/util-pipes';
import { TemplateWithValueDirective } from '@coin/shared/core/util-directives';

export enum PublishLettersDialogMode {
  Publish = 'publish',
  Preset = 'preset'
}

interface DialogData {
  workflowType: WorkflowType;
  season: Season;
  mode?: PublishLettersDialogMode;
}
type DialogResult = LetterSelectionResult;

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-publish-letters-dialog',
  templateUrl: './publish-letters-dialog.component.html',
  styleUrls: ['./publish-letters-dialog.component.scss'],
  imports: [
    V2DialogModule,
    V2ButtonComponent,
    TranslateModule,
    LegacyComponentsModule,
    FormsModule,
    MatIcon,
    ReactiveFormsModule,
    V2DateInputComponent,
    V2CardComponent,
    MatTooltip,
    EnumToTextPipe,
    TemplateWithValueDirective,
    V2DropdownComponent
  ]
})
export class PublishLettersDialogComponent implements OnInit {
  data = inject<DialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<PublishLettersDialogComponent>>(MatDialogRef);
  private letterCreationService = inject(LetterCreationService);
  private budgetAllocationService = inject(BudgetAllocationService);
  private formBuilder = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  @Input() onlyContent: boolean;

  public dialogMode = PublishLettersDialogMode;

  public selection = new IncentiveLetterLetterPublishSelection();
  public selectedWorkflowId: string;
  public employeeMails: CoinLetterTemplate[] = [];
  public employeeTasks: CoinLetterTemplate[] = [];
  public managerMails: CoinLetterTemplate[] = [];
  public managerTasks: CoinLetterTemplate[] = [];
  public workflows: Workflow[] = [];
  public managerTypeEnum = IncentiveRecordsManagerTypeEnum;
  public managerType = IncentiveRecordsManagerTypeEnum.LineManager;
  public emptyOptionValue = { id: uuid() };
  public overriddenPublicationDates: AddPublicationDate[] = [];
  public overriddenPublicationDateFormControls: FormControl<Moment>[] = [];
  private presetInfo: CommunicationsLetterPublicationsPresetLetterPublicationInfoModel | null = null;

  public season: Season = this.data.season;
  private communicationDateSettings$ = this.budgetAllocationService.getSettings(this.season?.id).pipe(
    map(settings => settings?.communicationDateSettings),
    shareReplay({ bufferSize: 0, refCount: true })
  );

  public get isPublishDisabled(): boolean {
    if (this.noActionWorkflow) {
      return !this.selection?.employeeMail?.id || !this.selection?.managerMail?.id || !this.selectedWorkflowId;
    }
    if (this.onlyManagerWorkflow) {
      return !this.selection?.managerTask?.id || !this.selection?.managerMail?.id || !this.selectedWorkflowId;
    }
    if (this.onlyEmployeeWorkflow) {
      return (
        !this.selection?.employeeTask?.id ||
        !this.selection?.employeeMail?.id ||
        !this.selection?.managerMail?.id ||
        !this.selectedWorkflowId
      );
    }
    if (this.managerWorkflowWithEmployeeNotification) {
      return (
        !this.selection?.employeeMail?.id ||
        !this.selection?.managerMail?.id ||
        !this.selection?.managerTask?.id ||
        !this.selectedWorkflowId
      );
    }

    return (
      !this.selection?.employeeTask?.id ||
      !this.selection?.employeeMail?.id ||
      !this.selection?.managerTask?.id ||
      !this.selection?.managerMail?.id ||
      !this.selectedWorkflowId
    );
  }

  public get selectedType(): CommunicationWorkflowType {
    return this.workflows?.find(workflow => workflow.id === this.selectedWorkflowId)?.type;
  }

  public get onlyEmployeeWorkflow(): boolean {
    return WorkflowHelper.isEmployeeWorkflow(this.selectedType);
  }

  public get onlyManagerWorkflow(): boolean {
    return WorkflowHelper.isManagerWorkflow(this.selectedType);
  }

  public get managerWorkflowWithEmployeeNotification(): boolean {
    return WorkflowHelper.isManagerWorkflowWithEmployeeNotification(this.selectedType);
  }

  public get noActionWorkflow(): boolean {
    return WorkflowHelper.isNoActionWorkflow(this.selectedType);
  }

  public get isEmployeeConfirmationWorkflow(): boolean {
    return this.selectedType === CommunicationWorkflowType.EmployeeConfirmation;
  }

  public static openDialog(dialog: MatDialog, data?: DialogData): Observable<DialogResult> {
    return dialog
      .open<PublishLettersDialogComponent, DialogData, DialogResult>(PublishLettersDialogComponent, {
        disableClose: true,
        data
      })
      .beforeClosed();
  }

  async ngOnInit(): Promise<void> {
    const workflows$ = this.letterCreationService.getWorkflows(this.data?.workflowType ?? WorkflowType.Standard);
    // In preset mode the dialog prefills the stored publication information so it can be reviewed and updated.
    const presetInfo$ =
      this.data?.mode === PublishLettersDialogMode.Preset
        ? this.letterCreationService.getPresetLetterPublicationInfo(this.season?.id, this.season?.seasonType)
        : of(null);

    forkJoin([workflows$, presetInfo$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([workflows, presetInfo]) => {
        this.workflows = workflows;
        this.presetInfo = presetInfo;
        this.selectDefaultWorkflow();
        this.applyPresetWorkflowSelection();
        this.loadWorkflowTemplates();
      });
  }

  private applyPresetWorkflowSelection(): void {
    if (!this.presetInfo) return;

    if (this.workflows?.some(workflow => workflow.id === this.presetInfo.workflowId)) {
      this.selectedWorkflowId = this.presetInfo.workflowId;
    }
    const presetManagerType =
      IncentiveRecordsManagerTypeEnum[this.presetInfo.managerType as keyof typeof IncentiveRecordsManagerTypeEnum];
    if (presetManagerType) {
      this.managerType = presetManagerType;
    }
  }

  private selectDefaultWorkflow(): void {
    switch (this.season?.seasonType) {
      case SeasonTypeEnum.SOG:
        this.selectedWorkflowId = this.workflows.find(
          workflow => workflow.type === CommunicationWorkflowType.ManagerConfirmationEmployeeSignature
        )?.id;
        break;
      case SeasonTypeEnum.SpecialPayment:
      case SeasonTypeEnum.SpecialPaymentCash:
      case SeasonTypeEnum.SpecialPaymentEquity:
        this.selectedWorkflowId = this.workflows.find(
          workflow => workflow.type === CommunicationWorkflowType.NoActionNeeded
        )?.id;
        break;
      default:
        this.selectedWorkflowId = this.workflows?.[0]?.id;
    }

    this.initializeOverriddenPublicationDates();
  }

  public loadWorkflowTemplates(): void {
    this.selection = new IncentiveLetterLetterPublishSelection();

    const requests$ = [
      this.letterCreationService.getFilteredWorkflowTemplates(
        IncentiveRecordsWorkflowTemplateTypeEnum.EmployeeTask,
        this.selectedWorkflowId
      ),
      this.letterCreationService.getFilteredWorkflowTemplates(
        IncentiveRecordsWorkflowTemplateTypeEnum.EmployeeMail,
        this.selectedWorkflowId
      ),
      this.letterCreationService.getFilteredWorkflowTemplates(
        IncentiveRecordsWorkflowTemplateTypeEnum.ManagerTask,
        this.selectedWorkflowId
      ),
      this.letterCreationService.getFilteredWorkflowTemplates(
        IncentiveRecordsWorkflowTemplateTypeEnum.ManagerMail,
        this.selectedWorkflowId
      )
    ];

    forkJoin(requests$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([employeeTasks, employeeMails, managerTasks, managerMails]) => {
        this.employeeTasks = employeeTasks?.filter(item => item?.state === CommunicationStateEnum.Published);
        this.employeeMails = employeeMails?.filter(item => item?.state === CommunicationStateEnum.Published);
        this.managerTasks = managerTasks?.filter(item => item?.state === CommunicationStateEnum.Published);
        this.managerMails = managerMails?.filter(item => item?.state === CommunicationStateEnum.Published);
        this.applyPresetTemplateSelection();
      });

    this.initializeOverriddenPublicationDates();
  }

  private initializeOverriddenPublicationDates(): void {
    this.communicationDateSettings$
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe(communicationDateSettings => {
        this.overriddenPublicationDates = communicationDateSettings
          ?.filter(s => s.workflowType === this.selectedType)
          .map(
            s =>
              ({
                workflowType: s.workflowType,
                fromLetterState: s.fromLetterState,
                toLetterState: s.toLetterState,
                publicationDate: this.getPresetPublicationDate(s.workflowType, s.fromLetterState, s.toLetterState)
              }) as AddPublicationDate
          );
        this.overriddenPublicationDateFormControls = this.overriddenPublicationDates?.map(overridenPublicationDate =>
          this.formBuilder.control(moment(overridenPublicationDate.publicationDate))
        );
      });
  }

  private getPresetPublicationDate(workflowType: string, fromLetterState: string, toLetterState: string): string {
    return (
      this.presetInfo?.overriddenPublicationDates?.find(
        preset =>
          preset.workflowType === workflowType &&
          preset.fromLetterState === fromLetterState &&
          preset.toLetterState === toLetterState
      )?.publicationDate ?? ''
    );
  }

  private applyPresetTemplateSelection(): void {
    if (!this.presetInfo) return;

    this.presetTemplate('employeeTask', this.employeeTasks, this.presetInfo.employeeTaskTemplateId);
    this.presetTemplate('employeeMail', this.employeeMails, this.presetInfo.employeeMailTemplateId);
    this.presetTemplate('managerTask', this.managerTasks, this.presetInfo.managerTaskTemplateId);
    this.presetTemplate('managerMail', this.managerMails, this.presetInfo.managerMailTemplateId);
  }

  private presetTemplate(
    key: keyof IncentiveLetterLetterPublishSelection,
    templates: CoinLetterTemplate[],
    templateId: string | null
  ): void {
    const template = templates?.find(item => item.id === templateId);
    if (template) {
      this.selection[key] = template as IncentiveLetterLetterPublishSelection[typeof key];
    }
  }

  public selectManagerType(type: IncentiveRecordsManagerTypeEnum): void {
    this.managerType = type;
  }

  public publish(): void {
    this.dialogRef.close(this.getSelectedPublishProperties());
  }

  public getSelectedPublishProperties(): LetterSelectionResult {
    const managerIndex = Object.values(IncentiveRecordsManagerTypeEnum).findIndex(value => value === this.managerType);
    const selection = Object.entries(this.selection)
      .filter(([_, value]) => value && value.id !== this.emptyOptionValue.id)
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as IncentiveLetterLetterPublishSelection);

    return {
      selection,
      workflowId: this.selectedWorkflowId,
      managerType: (Object.keys(IncentiveRecordsManagerTypeEnum) as IncentiveRecordsManagerTypeEnum[])[managerIndex],
      overriddenPublicationDates:
        this.overriddenPublicationDates
          ?.map((overridenPublicationDate, i) => ({
            ...overridenPublicationDate,
            publicationDate: this.overriddenPublicationDateFormControls[i].value.toISOString()
          }))
          .filter(d => !!d.publicationDate) ?? []
    };
  }

  public close(): void {
    this.dialogRef.close();
  }

  public displayFn(item: CoinLetterTemplate): string {
    return item.title;
  }

  public displayWorkflowFn(item: Workflow): string {
    return TinyHelpers.pascalcaseToText(item.type);
  }
}
